import {MqttDeviceWrapper, MqttEnergyProfile, MqttMeasures} from '../mqtt';
import {Device, DeviceType, DinoPlusMeasureType, Measure} from 'smag-commons/cfg/connector/mqtt';
import {MqttDinoPlusMeasureTypePipe} from '../connector.pipe';
import * as _ from 'lodash';

export class DinoPlusMqttDeviceWrapper implements MqttDeviceWrapper<DinoPlusMeasureType> {

    private readonly mtp: MqttDinoPlusMeasureTypePipe;

    deviceType = DeviceType.DINOPLUS_MOME;

    maxProductionPlants = 1;

    measureTypes: Set<DinoPlusMeasureType>;

    constructor(private readonly devId: string,
                private readonly device: Device) {
        this.mtp = new MqttDinoPlusMeasureTypePipe();
        this.measureTypes = new Set([
            ...ABSORPTION_MEASURE_TYPES,
            ...INJECTION_MEASURE_TYPES,
            ..._.flatMap(PRODUCTION_MEASURE_TYPES, mt => Array.from(mt))
        ]);
    }

    getMqttMeasures(): MqttMeasures {
        const absorptionMeasures: string[] = [];
        const injectionMeasures: string[] = [];
        const productionMeasures: string[][] = [];
        for (let i = 0; i < this.maxProductionPlants; ++i) {
            productionMeasures[i] = [];
        }
        const measures = this.device.measures || {};
        for (let measId in measures) {
            const meas = measures[measId];
            if (ABSORPTION_MEASURE_TYPES.has(meas.dinoplus_meas_type)) {
                absorptionMeasures.push(measId);
            } else if (INJECTION_MEASURE_TYPES.has(meas.dinoplus_meas_type)) {
                injectionMeasures.push(measId);
            } else {
                for (let i = 0; i < this.maxProductionPlants; ++i) {
                    if (PRODUCTION_MEASURE_TYPES[i].has(meas.dinoplus_meas_type)) {
                        productionMeasures[i].push(measId);
                    }
                }
            }
        }
        return {
            absorptionMeasures,
            injectionMeasures,
            productionMeasures
        };
    }

    getMeasureType(measId: string): DinoPlusMeasureType {
        return (this.device.measures || {})[measId]?.dinoplus_meas_type;
    }

    setMeasureType(measId: string, measureType: DinoPlusMeasureType) {
        if (!this.device.measures) {
            this.device.measures = {};
        }
        if (!this.device.measures[measId]) {
            this.device.measures[measId] = {};
        }
        this.device.measures[measId].dinoplus_meas_type = measureType;
    }

    setDefaultMeasures(profile: MqttEnergyProfile, productionPods?: string[]) {
        // NOTE: we make deep copies, as we do not want to risk overwriting the archetypes
        let measures = [];
        if (profile === MqttEnergyProfile.CONSUMER) {
            measures = _.cloneDeep(ABSORPTION_MEASURES).map(m => [m, null]);
        } else if (profile === MqttEnergyProfile.PRODUCER_PROSUMER) {
            measures = [
                ..._.cloneDeep(ABSORPTION_MEASURES).map(m => [m, 0, null]),
                ..._.cloneDeep(INJECTION_MEASURES).map(m => [m, 0, null]),
                ..._.cloneDeep(PRODUCTION_MEASURES).map(m => [m, 1, productionPods[0]])
            ];
        }
        this.device.measures = {};
        (measures as [Measure, number, string][]).forEach(([meas, plantIndex, prodPod]) => {
            // if we do not have a production POD, we use an index in order to ensure uniqueness of `measId`
            const prodStr = plantIndex > 0 ? prodPod || `${plantIndex}` : null;
            const measId = `${this.devId}${prodStr ? '_' + prodStr : ''}_${MEASURE_SUFFIXES[meas.dinoplus_meas_type]}`;
            this.device.measures[measId] = meas;
        });
    }

    formatMeasureType(measureType: DinoPlusMeasureType): string {
        return this.mtp.transform(measureType);
    }
}

const ABSORPTION_MEASURES: Measure[] = [
    {
        uom: 'kW',
        dinoplus_meas_type: DinoPlusMeasureType.AVERAGE_ABSORBED_POWER,
        expression: 'v/1000'
    },
    {
        uom: 'kWh',
        dinoplus_meas_type: DinoPlusMeasureType.ABSORBED_ENERGY,
        expression: 'v/1000'
    },
    {
        uom: 'kWh',
        dinoplus_meas_type: DinoPlusMeasureType.TOTAL_ABSORBED_ENERGY,
        expression: 'v/1000'
    }
];

const INJECTION_MEASURES: Measure[] = [
    {
        uom: 'kW',
        dinoplus_meas_type: DinoPlusMeasureType.AVERAGE_INJECTED_POWER,
        expression: 'v/1000'
    },
    {
        uom: 'kWh',
        dinoplus_meas_type: DinoPlusMeasureType.INJECTED_ENERGY,
        expression: 'v/1000'
    },
    {
        uom: 'kWh',
        dinoplus_meas_type: DinoPlusMeasureType.TOTAL_INJECTED_ENERGY,
        expression: 'v/1000'
    }
];

const PRODUCTION_MEASURES: Measure[] = [
    {
        uom: 'kW',
        dinoplus_meas_type: DinoPlusMeasureType.AVERAGE_PRODUCED_POWER,
        expression: 'v/1000'
    },
    {
        uom: 'kWh',
        dinoplus_meas_type: DinoPlusMeasureType.PRODUCED_ENERGY,
        expression: 'v/1000'
    },
    {
        uom: 'kWh',
        dinoplus_meas_type: DinoPlusMeasureType.TOTAL_PRODUCED_ENERGY,
        expression: 'v/1000'
    }
];

const ABSORPTION_MEASURE_TYPES: Set<DinoPlusMeasureType> = new Set(ABSORPTION_MEASURES.map(m => m.dinoplus_meas_type));
const INJECTION_MEASURE_TYPES: Set<DinoPlusMeasureType> = new Set(INJECTION_MEASURES.map(m => m.dinoplus_meas_type));
const PRODUCTION_MEASURE_TYPES: Set<DinoPlusMeasureType>[] = [
    new Set(PRODUCTION_MEASURES.map(m => m.dinoplus_meas_type))
];

const MEASURE_SUFFIXES: { [measType in DinoPlusMeasureType]: string } = {
    [DinoPlusMeasureType.AVERAGE_ABSORBED_POWER]: 'pot_prel_med_15m',
    [DinoPlusMeasureType.ABSORBED_ENERGY]: 'en_prel_15m',
    [DinoPlusMeasureType.TOTAL_ABSORBED_ENERGY]: 'en_prel_tot',
    [DinoPlusMeasureType.AVERAGE_INJECTED_POWER]: 'pot_imm_med_15m',
    [DinoPlusMeasureType.INJECTED_ENERGY]: 'en_imm_15m',
    [DinoPlusMeasureType.TOTAL_INJECTED_ENERGY]: 'en_imm_tot',
    [DinoPlusMeasureType.AVERAGE_PRODUCED_POWER]: 'pot_gen_med_15m',
    [DinoPlusMeasureType.PRODUCED_ENERGY]: 'en_gen_15m',
    [DinoPlusMeasureType.TOTAL_PRODUCED_ENERGY]: 'en_gen_tot'
};
